import { Banner } from '@/components/banner/banner.component';
import { Loading } from '@/components/loading';
import { Body3, H3 } from '@/components/typography';
import { cn } from '@/lib/utils';
import { usePeerGroupCarbonStockData } from '@/modules/Report/Report.api';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { Bar, CartesianGrid, ComposedChart, LabelList, Legend, ResponsiveContainer, Scatter, XAxis, YAxis } from 'recharts';
import type { ScatterPointItem } from 'recharts/types/cartesian/Scatter';
import { LabelPosition } from 'recharts/types/component/Label';

type MultiBarScatterChartProps = {
  farmId: string;
  fieldId: string;
  className?: string;
};

function roundTo1SigFig(num: number): number {
  if (num === 0) return 0;
  const power = Math.floor(Math.log10(Math.abs(num)));
  const factor = Math.pow(10, power);
  return Math.ceil(num / factor) * factor;
}

export const MultiBarScatterChart: React.FC<MultiBarScatterChartProps> = ({ farmId, fieldId, className }) => {
  const { data: multiBarScatterChartData, status } = usePeerGroupCarbonStockData(farmId, fieldId);
  if (status === 'pending') return <Loading />;
  if (status === 'error') {
    toast.error('Failed to fetch bar chart data');
    return;
  }

  // Convert string values to numbers, preserving decimal precision
  const stockRangeMin = multiBarScatterChartData.result_set_1[0]?.stock_range_min
    ? Number(multiBarScatterChartData.result_set_1[0].stock_range_min)
    : undefined;
  const stockRangeMax = multiBarScatterChartData.result_set_1[0]?.stock_range_max
    ? Number(multiBarScatterChartData.result_set_1[0].stock_range_max)
    : undefined;
  const interval = multiBarScatterChartData.result_set_1[0]?.stock_range_interval
    ? Number(multiBarScatterChartData.result_set_1[0].stock_range_interval)
    : undefined;
  const frequencyMax = Math.max(
    ...multiBarScatterChartData.result_set_2.map((item) => {
      const freq = item.freq;
      return typeof freq === 'string' ? Number((freq as string).replace(/,/g, '')) : freq;
    })
  );
  const peerMeanCarbonStock = multiBarScatterChartData.result_set_3[0]?.peer_mean_carbon_stock_tpha
    ? Number(multiBarScatterChartData.result_set_3[0].peer_mean_carbon_stock_tpha)
    : undefined;
  const fieldMeanCarbonStock = multiBarScatterChartData.result_set_4[0]?.mean_carbon_stock_tpha
    ? Number(multiBarScatterChartData.result_set_4[0].mean_carbon_stock_tpha)
    : undefined;
  const scatterYValue = frequencyMax * 1.2;

  const yAxisMax = roundTo1SigFig(Math.max(frequencyMax * 1.3, scatterYValue * 1.1)); // Ensures scatter points are always visible

  if (
    stockRangeMin === undefined ||
    stockRangeMax === undefined ||
    interval === undefined ||
    frequencyMax === undefined ||
    peerMeanCarbonStock === undefined ||
    fieldMeanCarbonStock === undefined
  ) {
    return null;
  }

  // Positioning scatter plot labels to avoid overlap
  const carbonStockDifference = Math.abs(fieldMeanCarbonStock - peerMeanCarbonStock);
  const carbonStockDifferencePercentage = (carbonStockDifference / stockRangeMax) * 100;

  let peerMeanStockLabelPosition = 'left' as LabelPosition;
  if (carbonStockDifferencePercentage < 10) {
    peerMeanStockLabelPosition = 'top';
  } else if ((peerMeanCarbonStock / stockRangeMax) * 100 > 90) {
    peerMeanStockLabelPosition = 'left';
  } else if (peerMeanCarbonStock > fieldMeanCarbonStock) {
    peerMeanStockLabelPosition = 'right';
  } else if (stockRangeMin === peerMeanCarbonStock || (peerMeanCarbonStock / stockRangeMax) * 100 < 5) {
    peerMeanStockLabelPosition = 'right';
  }

  let fieldMeanStockLabelPosition = 'left' as LabelPosition;
  if (carbonStockDifferencePercentage < 10) {
    fieldMeanStockLabelPosition = 'bottom';
  } else if ((fieldMeanCarbonStock / stockRangeMax) * 100 > 90) {
    fieldMeanStockLabelPosition = 'left';
  } else if (fieldMeanCarbonStock > peerMeanCarbonStock) {
    fieldMeanStockLabelPosition = 'right';
  } else if (stockRangeMin === fieldMeanCarbonStock || (fieldMeanCarbonStock / stockRangeMax) * 100 < 5) {
    fieldMeanStockLabelPosition = 'right';
  }

  let peerLabeldx = peerMeanStockLabelPosition === 'top' ? 0 : peerMeanStockLabelPosition === 'left' ? -5 : 5;
  const peerLabeldy = peerMeanStockLabelPosition === 'top' ? -5 : 0;
  let fieldLabeldx = fieldMeanStockLabelPosition === 'bottom' ? 0 : fieldMeanStockLabelPosition === 'left' ? -5 : 5;
  const fieldLabeldy = fieldMeanStockLabelPosition === 'bottom' ? 5 : 0;

  // Adjusting label x-axis so that the labels are always inside the chart's boundary without cutoff
  if (carbonStockDifferencePercentage < 15 && (peerMeanCarbonStock / stockRangeMax) * 100 > 98) {
    peerLabeldx = -15;
  } else if (carbonStockDifferencePercentage < 15 && (peerMeanCarbonStock / stockRangeMax) * 100 < 1) {
    peerLabeldx = 5;
  }

  if (carbonStockDifferencePercentage < 15 && (fieldMeanCarbonStock / stockRangeMax) * 100 > 98) {
    fieldLabeldx = -15;
  } else if (carbonStockDifferencePercentage < 15 && (fieldMeanCarbonStock / stockRangeMax) * 100 < 1) {
    fieldLabeldx = 10;
  }

  // Formatting data for the bar chart and scatter plot
  const formattedData = multiBarScatterChartData.result_set_2.map((item, index) => {
    const freq: string | number | undefined = item?.freq;
    return {
      name: item?.stock_range,
      barValue: typeof freq === 'string' ? Number((freq as string).replace(/,/g, '')) : freq,
      ...(index === 0 && {
        scatterYValue: scatterYValue,
        scatterXValue: peerMeanCarbonStock,
        scatterColor: '#81C9BE',
      }),
    };
  });

  const adjustedScatterYValue = scatterYValue * 0.95;

  const formattedData2ndScatterPoint = [
    {
      scatterYValue: peerMeanCarbonStock == fieldMeanCarbonStock ? adjustedScatterYValue : scatterYValue,
      scatterXValue: fieldMeanCarbonStock,
      scatterColor: '#D79C6E',
    },
  ];

  return (
    <>
      <div className={clsx('flex justify-between', className)}>
        <div className={cn('flex flex-col gap-4')}>
          <H3 className={cn('font-bold')}>How does your field compare?</H3>
        </div>
        <div className={cn('flex flex-col gap-1 border rounded-lg border-neutral-400 p-3')}>
          <div className={cn('flex items-center gap-2')}>
            <div className={cn('bg-graph-bar-bg h-4 w-4 border-2 border-graph-bar-border')}></div>
            <Body3>Peer group distribution</Body3>
          </div>
          <div className={cn('flex items-center gap-2')}>
            <div className={cn('bg-secondary h-4 w-4 rounded-full')}></div>
            <Body3>Field mean carbon stock (t/ha)</Body3>
          </div>
          <div className={cn('flex items-center gap-2')}>
            <div className={cn('bg-graph-bar-bg h-4 w-4 rounded-full')}></div>
            <Body3>Peer group mean carbon stock (t/ha) </Body3>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={formattedData}
          margin={{
            top: 0,
            right: 0,
            bottom: 15,
            left: 10,
          }}
        >
          <CartesianGrid horizontal={false} vertical={false} />
          {/* Bar chart X-axis */}
          <XAxis type="category" dataKey="name" hide={true} />
          {/* Scatter chart X-axis */}
          <XAxis
            xAxisId="scatterXAxis"
            type="number"
            dataKey="scatterXValue"
            domain={[stockRangeMin, stockRangeMax]}
            hide={false}
            ticks={Array.from({ length: (stockRangeMax - stockRangeMin) / interval + 1 }, (_, i) => stockRangeMin + i * interval)} // Generates ticks based on inteval value received from API
            strokeWidth={2}
            label={{
              value: 'Average Carbon Stock (t/ha)',
              dy: 20,
              dx: 0,
            }}
          />
          <YAxis
            domain={[0, yAxisMax]}
            tick={true}
            label={{
              value: `Frequency (Number of fields)`,
              angle: -90,
              position: 'insideLeft',
              dy: 110,
              dx: 0,
            }}
            strokeWidth={2}
          />
          <Legend />
          {/* Bar chart */}
          <Bar dataKey="barValue" barSize={50} fill="#81C9BE" stroke="#43A1B5" strokeWidth={2} legendType="none" />
          {/* 2 Scatter charts needed becasue they require different <LabelList> position props */}
          {/* Scatter point 1: peerMeanStock*/}
          <Scatter
            data={formattedData}
            dataKey="scatterYValue"
            xAxisId="scatterXAxis"
            shape={(props: ScatterPointItem) => {
              const { cx, cy, payload } = props;
              if (!cx || !cy) return <></>;
              const scatterColor = payload?.scatterColor || '#000';
              return <circle cx={cx} cy={cy} r={10} fill={scatterColor} />;
            }}
            legendType="none"
          >
            {/* Peer scatter point label */}
            <LabelList
              dataKey="scatterXValue"
              //Label posiiton and margin based on the difference between peerMeanStock and fieldMeanStock
              position={peerMeanStockLabelPosition}
              dx={peerLabeldx}
              dy={peerLabeldy}
              style={{ fontSize: '16px', fill: '#6faea5' }}
            />
          </Scatter>
          {/* Scatter point 2: fieldMeanStock*/}
          <Scatter
            data={formattedData2ndScatterPoint}
            dataKey="scatterYValue"
            xAxisId="scatterXAxis"
            shape={(props: ScatterPointItem) => {
              const { cx, cy, payload } = props;
              if (!cx || !cy) return <></>;
              const scatterColor = payload?.scatterColor || '#000';
              return <circle cx={cx} cy={cy} r={10} fill={scatterColor} />;
            }}
            legendType="none"
          >
            {/* Field scatter point label */}
            <LabelList
              dataKey="scatterXValue"
              //Label posiiton and margin based on the difference between peerMeanStock and fieldMeanStock
              position={fieldMeanStockLabelPosition}
              dx={fieldLabeldx}
              dy={fieldLabeldy}
              style={{ fontSize: '16px', fill: '#D79C6E' }}
            />
          </Scatter>
        </ComposedChart>
      </ResponsiveContainer>
      {multiBarScatterChartData.result_set_5 && multiBarScatterChartData.result_set_5[0]?.peer_group_comment && (
        <Banner
          className={'mt-4'}
          text={`The soil carbon stock in the 0-30cm depth layer in this field is ${multiBarScatterChartData.result_set_5[0]?.peer_group_comment} compared to
              your peer group of ${multiBarScatterChartData.result_set_5[0]?.peer_population} fields in the Agricarbon database that are in a 50 mile radius of this field and in a similar land use.`}
        />
      )}
    </>
  );
};
